/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import {
  API,
  defaultLimitPagination,
  fileUpload,
  get,
  incidentMethodType,
  incidentTypeOptions,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { useLocation, useNavigate } from "react-router-dom";
import {
  formatName,
  formatSubcontractorName,
  getModuleNamingBySlug,
} from "../../../lib/Misc";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import { debounce } from "lodash";
import IncidentActionButton from "./IncidentActionButton";
import MainBody from "../../../components/MainBody/MainBody";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import AddEditIncident from "./AddEditIncident";
import { LucidePlusCircle } from "lucide-react";
import sortCaret from "../../../components/SortCaret";
import { useTrust } from "../../../lib/context/TrustContext";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import { Checkbox } from "../../../components/ui/checkbox";
import { useUser } from "../../../lib/context/UserContext";
import moment from "moment";
import UnauthorisedAccess from "../../../UnauthorisedAccess";

// * Start: Incident Listing Page
const Incidents = ({ readAccess, writeAccess }) => {
  // * Start: Url Management State

  const { userData } = useUser();
  const { organizationData } = useOrganization();

  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");

  // * End: Url Management State

  // * Start: List Management State

  const { trustId } = useTrust();
  const [incidentsList, setIncidentsList] = useState([]);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  // * End: List Management State

  // * Start: Single Incident Action State

  const [edit, setEdit] = useState(false);
  const [incidentId, setIncidentId] = useState(null);
  const [showAddEditIncident, setShowAddEditIncident] = React.useState(false);

  // * End: Single Incident Action State

  const toggleShowAddEditIncident = () => {
    if (showAddEditIncident) {
      setEdit(false);
      setIncidentId(null);
    }
    setShowAddEditIncident(!showAddEditIncident);
  };

  /**
   * * Start: Fetch Incident List Form The API
   * @param {Number} passLimit (Number of Limit for each page)
   * @param {Number} passPage (Number of Page for records)
   * @returns {Array} Incident List
   **/

  const fetchIncidents = useCallback(
    async (passLimit, passPage) => {
      try {
        setIncidentLoading(true);
        const { data } = await get(
          `${API.GET_INCIDENTS}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&trust_id=${trustId}&organization_id=${
            organizationData?.organizationId
              ? organizationData?.organizationId
              : ""
          }`,
        );
        setIncidentLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const incidentsList = data.data.data;
        setIncidentsList(incidentsList);
        return data.data;
      } catch (error) {
        setIncidentsList([]);
        setIncidentLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId],
  );

  //* End: Fetch Incident List Form The API

  const handleSubmitted = async (submitted, id) => {
    try {
      setIncidentLoading(true);
      const formData = new FormData();
      formData.append("submitted", submitted ? 1 : 0);
      await fileUpload(`${API.INCIDENT_SUBMITTED}/${id}`, formData);
      setIncidentLoading(false);
      fetchIncidents();
    } catch (error) {
      setIncidentLoading(false);
    }
  };

  /**
   * * Start: Incident Table List Record Generation
   * @param {Number} quantity (Number of Incidents)
   * @returns {Array} Incident List
   **/

  const incidentsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: incidentsList[i]?.id,
        id: incidentsList[i]?.id ? incidentsList[i]?.id : "",
        incident_type: incidentsList[i]?.incident_type
          ? incidentTypeOptions?.find(
              (item) => item.value == incidentsList[i]?.incident_type,
            )?.label
          : "",
        contact_name: formatSubcontractorName(incidentsList[i]?.contactable),
        contactable_type: incidentsList[i]?.contactable_type
          ? incidentsList[i]?.contactable_type === "vendor"
            ? getModuleNamingBySlug(
                organizationData?.organizationData,
                "vendors",
                "Vendor",
              )
            : incidentsList[i]?.contactable_type === "contact"
              ? getModuleNamingBySlug(
                  organizationData?.organizationData,
                  "contacts",
                  "Customer",
                )
              : ""
          : "",
        transaction_date: incidentsList[i]?.transaction_date
          ? incidentsList[i]?.transaction_date
          : "",
        method: incidentsList[i]?.method
          ? incidentMethodType?.find(
              (item) => item.value == incidentsList[i]?.method,
            )?.label
          : "",
        amount: incidentsList[i]?.amount ? incidentsList[i]?.amount : "",
        verification_date: incidentsList[i]?.verification_date
          ? incidentsList[i]?.verification_date
          : "",
        verified_by: incidentsList[i]?.verified_by
          ? formatSubcontractorName(incidentsList[i]?.verified_by)
          : "",
        approved_date: incidentsList[i]?.approved_date
          ? incidentsList[i]?.approved_date
          : "",
        approved_by: incidentsList[i]?.approved_by
          ? formatSubcontractorName(incidentsList[i]?.approved_by)
          : "",
        action: (
          <IncidentActionButton
            id={incidentsList[i]?.id}
            setEdit={setEdit}
            setIncidentId={setIncidentId}
            toggleShowAddEditIncident={toggleShowAddEditIncident}
            trustId={trustId}
            incident={incidentsList[i]}
            approved={incidentsList[i]?.approved}
            fetchIncidents={() => fetchIncidents()}
            approvalPremission={
              incidentsList[i]?.approval_permission
                ? incidentsList[i]?.approval_permission
                : ""
            }
            setLoading={setIncidentLoading}
          />
        ),
        submitted: (
          <div className={`flex justify-center`}>
            <Checkbox
              checked={incidentsList[i]?.submitted ? true : false}
              onCheckedChange={() =>
                handleSubmitted(
                  incidentsList[i]?.submitted ? false : true,
                  incidentsList[i]?.id,
                )
              }
            />
          </div>
        ),
      });
    }
    return items;
  };

  const incidents = incidentsGenerator(incidentsList?.length);

  //* End: Incident Table List Record Generation

  //* Start: Incident List Columns

  const columns = [
    {
      dataField: "incident_type",
      text: "Incident Type",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "contactable_type",
      text: "Type",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "contact_name",
      text: "Contact Name",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "transaction_date",
      text: "Incident Date",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "method",
      text: "Method",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "amount",
      text: "Amount",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "verification_date",
      text: "Verification Date",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "verified_by",
      text: "Verified By",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "approved_date",
      text: "Approved Date",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "approved_by",
      text: "Approved By",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },

    {
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      // style: { width: "127px" },
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "submitted",
      text: "Submitted",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
      headerClasses: THClasses,
      classes: TDClasses,
    },
  ];

  //* End: Incident List Columns

  /**
   * * Start: Incident List Sorting
   * @param {type} (Sort)
   * @param {JSON} (page, sortField, sortOrder) (To get the current value of the table)
   **/
  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC",
      );
    }
  };

  //* End: Incident List Sorting

  /**
   * * Start: Incident List Change Limit
   * @param {Event} e (Limit Number)
   **/
  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchIncidents(limit, 1);
  };

  //* End: Incident List Change Limit

  /**
   * * Start: Incident List Change Page
   * @param {Number} page (Page Number)
   **/
  const handleChangePage = (page) => {
    setPage(page);
    fetchIncidents(limit, page);
  };

  //* End: Incident List Change Page

  //* Start: Incident List and URL Mangement useEffect

  useEffect(() => {
    navigate(
      `?tab=incidents&limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      },
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  //* End: Incident List and URL Mangement useEffect

  //* Start: Incident List Search Record Management

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    [],
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    if (nextValue?.length >= 2 || nextValue?.length === 0) {
      debouncedSave(nextValue);
    }
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  //* End: Incident List Search Record Management

  return readAccess ? (
    <MainBody
      searchParamData={searchParamData}
      handleClearSearch={handleClearSearch}
      handleSearchChange={handleSearchChange}
      actionContent={
        <>
          {/* Add/Edit Incident Modal */}
          <ResponsiveDrawerForm
            description={""}
            popupType={"incidents"}
            title={
              <div className="flex items-center justify-between pr-4">
                <div>{edit ? "Edit Incident" : "Add Incident"}</div>
                <div className="flex items-center gap-4 text-sm">
                  <div>
                    Recorded By:{" "}
                    {formatName(userData?.first_name, userData?.last_name)}
                  </div>
                  <div>Record Date: {moment()?.format("DD-MM-YYYY")}</div>
                </div>
              </div>
            }
            handleOpen={() => {}}
            handleClose={() => {
              setEdit(false);
              setIncidentId(null);
              setShowAddEditIncident(false);
            }}
            buttonText={
              <div className="flex items-center gap-1">
                <LucidePlusCircle size={18} />
                <span>Add Incident</span>
              </div>
            }
            className={"overflow-hidden sm:max-w-4xl"}
            open={showAddEditIncident}
            buttonClassName={""}
            setOpen={setShowAddEditIncident}
            mobileCancelHidden={true}
            write={true}
          >
            <AddEditIncident
              edit={edit}
              toggleShowAddEditIncident={toggleShowAddEditIncident}
              incidentID={incidentId}
              fetchIncidents={fetchIncidents}
            />
          </ResponsiveDrawerForm>
        </>
      }
      loading={incidentLoading}
      innerBody={true}
      parentHeight={true}
      mainClassName="w-[calc(100%-3rem)]"
    >
      {/* Incident Table and Pagination */}
      <div className="custom-table">
        <div className="table-body-class">
          <BootstrapTable
            keyField="incident_id"
            remote
            data={incidents}
            columns={columns}
            onTableChange={handleTableChange}
            // expandRow={expandRow}
            rowClasses={TRClasses}
            noDataIndication="No Data Found"
            classes={TableClasses}
            wrapperClasses={TableWrapClasses}
          />
        </div>

        <Pagination
          total={total}
          limit={parseInt(limit)}
          currentPage={page}
          updateLimit={handleUpdateLimit}
          updatePage={handleChangePage}
          from={from}
          to={to}
        />
      </div>
    </MainBody>
  ) : (
    <UnauthorisedAccess expandList={true} popup={true} />
  );
};

export default Incidents;
// * End: Incident Listing Page
